import React, { useEffect, useMemo, useState } from 'react';
import { Chess } from 'chess.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil } from 'lucide-react';

// Editor AISLADO de Multiple Choice. No toca el tablero de explicación, PGN,
// ni Play with Computer. Reutiliza ChessBoardView (render) y helpers puros de
// conversión FEN <-> mapa de casillas (idénticos a los de Play Computer, aquí
// re-declarados como funciones puras sin acoplamiento a sistemas críticos).

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const FILES_STR = 'abcdefgh';
const PALETTE = [
  { key: 'K', label: '♔' }, { key: 'Q', label: '♕' }, { key: 'R', label: '♖' }, { key: 'B', label: '♗' }, { key: 'N', label: '♘' }, { key: 'P', label: '♙' },
  { key: 'k', label: '♚' }, { key: 'q', label: '♛' }, { key: 'r', label: '♜' }, { key: 'b', label: '♝' }, { key: 'n', label: '♞' }, { key: 'p', label: '♟' },
];
function fenToMap(fen) {
  try {
    const [pieces] = String(fen).split(' ');
    const ranks = pieces.split('/');
    const map = {};
    for (let r = 0; r < 8; r++) {
      let f = 0;
      for (const ch of ranks[r]) {
        if (/\d/.test(ch)) f += parseInt(ch, 10);
        else { map[FILES_STR[f] + (8 - r)] = ch; f++; }
      }
    }
    return map;
  } catch { return {}; }
}
function mapToFen(map, turn = 'w') {
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const p = map[FILES_STR[f] + r];
      if (p) { if (empty > 0) { row += empty; empty = 0; } row += p; }
      else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return `${ranks.join('/')} ${turn} - - 0 1`;
}
const fenTurn = (fen) => (String(fen).split(' ')[1] === 'b' ? 'b' : 'w');
const safeBoardFen = (fen) => { try { new Chess(fen); return fen; } catch { return mapToFen(fenToMap(fen), fenTurn(fen)); } };

// -------- Ventana independiente para editar la posición --------
function BoardEditorDialog({ open, onClose, initialFen, onSave }) {
  const [draft, setDraft] = useState(initialFen || START);
  const [tool, setTool] = useState(null); // null=mover · 'erase' · 'K'|'k'|...
  useEffect(() => { if (open) { setDraft(initialFen || START); setTool(null); } }, [open, initialFen]);

  const turn = fenTurn(draft);
  const boardFen = safeBoardFen(draft);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="mc-board-editor" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Editar tablero</DialogTitle>
          <DialogDescription>Coloca, mueve o elimina piezas. El FEN se genera automáticamente.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid place-items-center">
            <ChessBoardView
              fen={boardFen}
              boardWidth={340}
              arePiecesDraggable={tool === null}
              onPieceDrop={(from, to) => {
                const map = fenToMap(draft);
                const piece = map[from]; if (!piece) return false;
                delete map[from]; map[to] = piece;
                setDraft(mapToFen(map, turn));
                return true;
              }}
              onSquareClick={(sq) => {
                if (tool === null) return;
                const map = fenToMap(draft);
                if (tool === 'erase') delete map[sq]; else map[sq] = tool;
                setDraft(mapToFen(map, turn));
              }}
            />
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" data-testid="mc-preset-start" onClick={() => setDraft(START)}>Tablero</Button>
              <Button size="sm" variant="outline" data-testid="mc-preset-clear" onClick={() => setDraft(mapToFen({}, turn))}>Borrar tablero</Button>
              <Button size="sm" variant={turn === 'w' ? 'default' : 'outline'} data-testid="mc-turn-w" onClick={() => setDraft(mapToFen(fenToMap(draft), 'w'))}>Turno blancas</Button>
              <Button size="sm" variant={turn === 'b' ? 'default' : 'outline'} data-testid="mc-turn-b" onClick={() => setDraft(mapToFen(fenToMap(draft), 'b'))}>Turno negras</Button>
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1.5">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Herramienta</div>
              <div className="flex flex-wrap gap-1">
                <button type="button" data-testid="mc-tool-move" onClick={() => setTool(null)}
                  className={`h-7 px-2 rounded text-xs font-semibold border ${tool === null ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`}>Mover</button>
                <button type="button" data-testid="mc-tool-erase" onClick={() => setTool('erase')}
                  className={`h-7 px-2 rounded text-xs font-semibold border ${tool === 'erase' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`}>Borrar</button>
                {PALETTE.map(p => (
                  <button type="button" key={p.key} data-testid={`mc-tool-${p.key}`} onClick={() => setTool(p.key)} title={`Colocar ${p.key}`}
                    className={`h-7 w-7 rounded text-lg leading-none border ${tool === p.key ? 'bg-amber-400 border-amber-500' : 'bg-white border-slate-300 hover:bg-slate-100'}`}>{p.label}</button>
                ))}
              </div>
              <div className="text-[10px] text-slate-500"><b>Mover</b>: arrastra piezas. Elige una pieza y haz clic en una casilla para colocarla. <b>Borrar</b>: clic en la casilla.</div>
            </div>
            <div>
              <Label className="text-[11px]">FEN</Label>
              <Input data-testid="mc-board-fen" className="font-mono text-[11px]" value={draft} onChange={e => setDraft(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="mc-board-cancel" onClick={onClose}>Cancelar</Button>
          <Button data-testid="mc-board-save" className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => {
              try { new Chess(draft); } catch { /* posición no estándar; se valida al guardar la actividad */ }
              onSave(draft);
              toast.success('Posición guardada');
              onClose();
            }}>Guardar posición</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MultipleChoiceEditor({ form, setForm }) {
  const [boardOpen, setBoardOpen] = useState(false);
  const fenValid = useMemo(() => { try { new Chess(form.fen); return true; } catch { return false; } }, [form.fen]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Columna izquierda: título + tablero + FEN */}
      <div className="space-y-3">
        <div><Label>Título</Label><Input data-testid="act-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
        <div className="grid place-items-center rounded-xl bg-slate-50 border border-slate-200 p-3">
          {fenValid
            ? <ChessBoardView fen={form.fen} boardWidth={260} arePiecesDraggable={false} />
            : <div className="text-xs text-rose-500 py-10">FEN inválido</div>}
        </div>
        <Button type="button" variant="outline" data-testid="act-edit-board" className="w-full" onClick={() => setBoardOpen(true)}>
          <Pencil className="w-4 h-4 mr-2" /> Editar tablero
        </Button>
        <div>
          <Label className="text-[11px]">FEN (manual)</Label>
          <Input data-testid="act-fen" className="font-mono text-xs" value={form.fen} onChange={e => setForm(f => ({ ...f, fen: e.target.value }))} />
        </div>
      </div>

      {/* Columna derecha: pregunta + opciones + puntuación */}
      <div className="space-y-3">
        <div><Label>Pregunta</Label><Textarea data-testid="act-question" rows={2} value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} placeholder="En esta posición, ¿qué sucede?" /></div>
        <Label>Opciones (marca la correcta)</Label>
        {form.options.map((op, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="radio" name="correct" checked={Number(form.correct_index) === i} onChange={() => setForm(f => ({ ...f, correct_index: i }))} data-testid={`act-correct-${i}`} />
            <Input data-testid={`act-option-${i}`} value={op} onChange={e => setForm(f => { const o = [...f.options]; o[i] = e.target.value; return { ...f, options: o }; })} placeholder={`Opción ${i + 1}`} />
            {form.options.length > 2 && <button type="button" onClick={() => setForm(f => ({ ...f, options: f.options.filter((_, x) => x !== i), correct_index: 0 }))} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
        {form.options.length < 5 && <Button size="sm" variant="outline" data-testid="act-add-option" onClick={() => setForm(f => ({ ...f, options: [...f.options, ''] }))}><Plus className="w-3.5 h-3.5 mr-1" /> Opción</Button>}

        <div className="grid grid-cols-3 gap-3 pt-3 border-t">
          <div><Label>Puntaje</Label><Input data-testid="act-points" type="number" value={form.points} onChange={e => setForm(f => ({ ...f, points: e.target.value }))} /></div>
          <div><Label>Intentos</Label><Input data-testid="act-attempts" type="number" value={form.max_attempts} onChange={e => setForm(f => ({ ...f, max_attempts: e.target.value }))} /></div>
          <div><Label>Tiempo (seg)</Label><Input data-testid="act-time" type="number" value={form.time_seconds} onChange={e => setForm(f => ({ ...f, time_seconds: e.target.value }))} /></div>
        </div>
      </div>

      <BoardEditorDialog
        open={boardOpen}
        onClose={() => setBoardOpen(false)}
        initialFen={form.fen}
        onSave={(newFen) => setForm(f => ({ ...f, fen: newFen }))}
      />
    </div>
  );
}
